'use es6';

import { useEffect } from 'react';
import PropTypes from 'prop-types';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UISelect from 'UIComponents/input/UISelect';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { decodeSelectGroupedDashboardValue, useDashboardSearchOptions } from '../../../data/hooks/useDashboardSearchOptions';
import usePlaceReportOnSpecificDashboardProblem from '../problemHooks/usePlaceReportOnSpecificDashboardProblem';
import { LOADING, NO_DASHBOARD_SELECTED, WILL_EXCEED_DASHBOARD_MAX_WIDGETS } from '../../problems';
import { noop } from '../../shared/noop';
import { jsx as _jsx } from "react/jsx-runtime";
const PAGINATION_SIZE = 100;
const DashboardSelect = ({
  groupedDashboardOptions,
  onChange,
  selectedDashboardValue,
  showLabel,
  validationMessage
}) => {
  const loadPaginatedOptions = (input, callback, pagination = {
    offset: 0
  }) => {
    const optionsFilteredBySearchInput = groupedDashboardOptions.reduce((filteredGroupedOptions, group) => {
      const filteredOptions = group.options.filter(dashboard => UISelect.stripDiacritics(dashboard.text.toLowerCase()).includes(input));
      if (filteredOptions.length > 0) {
        return [...filteredGroupedOptions, ...[Object.assign({}, group, {
          options: filteredOptions
        })]];
      } else {
        return filteredGroupedOptions;
      }
    }, []);
    const nextPaginationOffset = pagination.offset + PAGINATION_SIZE;
    let usedItemsSlots = 0;
    let hasMore = false;
    const paginatedOptions = optionsFilteredBySearchInput.reduce((groupedOptions, group) => {
      const availableItemsSlots = nextPaginationOffset - usedItemsSlots;
      if (availableItemsSlots === 0) {
        hasMore = true;
        return groupedOptions;
      } else if (group.options.length < availableItemsSlots) {
        usedItemsSlots = usedItemsSlots + group.options.length;
        return [...groupedOptions, ...[group]];
      } else {
        let cutoff;
        if (group.options.length > availableItemsSlots) {
          hasMore = true;
          cutoff = availableItemsSlots;
        } else {
          cutoff = group.options.length;
        }
        usedItemsSlots = usedItemsSlots + cutoff;
        return [...groupedOptions, ...[Object.assign({}, group, {
          options: group.options.slice(0, cutoff)
        })]];
      }
    }, []);
    callback(null, {
      options: paginatedOptions,
      pagination: {
        hasMore,
        offset: nextPaginationOffset
      }
    });
  };
  return /*#__PURE__*/_jsx(UIFormControl, {
    error: !!validationMessage,
    validationMessage: validationMessage,
    label: showLabel ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-action-components.dashboards.select"
    }) : null,
    "aria-label": I18n.text('reporting-action-components.dashboards.select'),
    children: /*#__PURE__*/_jsx(UISelect, {
      "data-test-id": "destination-dashboard-select",
      loadOptions: loadPaginatedOptions,
      options: groupedDashboardOptions,
      onSelectedOptionChange: ({
        target: {
          value: {
            value: newDashboardValue,
            text: newDashboardName
          }
        }
      }) => {
        if (newDashboardValue !== selectedDashboardValue) {
          const dashboardId = decodeSelectGroupedDashboardValue(newDashboardValue);
          onChange({
            dashboardId,
            dashboardName: newDashboardName
          });
        }
      },
      resultsClassName: "dashboards-search-results",
      value: selectedDashboardValue
    })
  });
};
const DashboardSelectWrapper = ({
  dashboardId,
  onChange,
  onValidationError = noop,
  savedReport,
  showLabel = true
}) => {
  const {
    data: groupedDashboardOptions,
    loading: dashboardOptionsLoading
  } = useDashboardSearchOptions();
  const {
    disabledMessage: validationError,
    problem
  } = usePlaceReportOnSpecificDashboardProblem({
    report: savedReport,
    targetDashboardId: dashboardId
  });

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    onValidationError(dashboardOptionsLoading ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-action-components.actions.loading"
    }) : validationError);
  }, [dashboardOptionsLoading, onValidationError, problem]);
  if (dashboardOptionsLoading) {
    return /*#__PURE__*/_jsx(UILoadingSpinner, {
      size: "xs"
    });
  }
  const encodedDashboardId = groupedDashboardOptions.reduce((foundValue, dashboardGroup) => {
    if (foundValue) {
      return foundValue;
    }
    const foundOption = dashboardGroup.options.find(option => option.value.endsWith(String(dashboardId)));
    return foundOption ? foundOption.value : null;
  }, null);
  const validationMessage = problem === LOADING || [NO_DASHBOARD_SELECTED, WILL_EXCEED_DASHBOARD_MAX_WIDGETS].includes(problem) ? null : validationError;
  return /*#__PURE__*/_jsx(DashboardSelect, {
    groupedDashboardOptions: groupedDashboardOptions,
    onChange: onChange,
    selectedDashboardValue: encodedDashboardId,
    showLabel: showLabel,
    validationMessage: validationMessage
  });
};
DashboardSelectWrapper.propTypes = {
  dashboardId: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  onValidationError: PropTypes.func,
  savedReport: PropTypes.object,
  showLabel: PropTypes.bool
};
export default DashboardSelectWrapper;